import { Action, ThunkAction } from '@reduxjs/toolkit'
import { FiscalYear } from '../../../types/fiscalYear/FiscalYear'
import { ReportDataType } from '../reports/types'
import {
  SetDataTypeFilterAction,
  SetFiscalYearFilterAction,
  SET_DATATYPE_FILTER,
  SET_FISCAL_YEAR_FILTER,
  ToggleDisplayEmptyRowsAction,
  TOGGLE_DISPLAY_EMPTY_ROWS,
  ToggleDataTypeFilterAction,
  TOGGLE_DATATYPE_FILTER,
  SetDimensionsFilterAction,
  SetBudgetingScenarioFilterAction,
  SET_BUDGETING_SCENARIO_FILTER,
  SetContextFilterAction,
  SET_CONTEXT_FILTER,
  SET_CUMULATIVE_FILTER,
  SetCumulativeFilterAction,
  SetPeriodGroupsAction,
  SET_PERIOD_GROUPS,
  AddPeriodGroupAction,
  ADD_PERIOD_GROUP,
  RemovePeriodGroupAction,
  REMOVE_PERIOD_GROUP,
  SET_PERIOD_GROUP_FILTER,
  SetPeriodGroupFilterAction,
  ToggleCumulativeFilterAction,
  TOGGLE_CUMULATIVE_FILTER,
  RESET_CONTEXT_FILTER,
  ResetContextFilterAction,
  SET_DIMENSIONS_FILTER,
  SetDimensionsQueryAction,
  SET_DIMENSIONS_QUERY,
  SetPeriodGroupSelectionAction,
  SET_PERIOD_GROUP_SELECTION,
  PeriodGroupSelection,
  SetFiscalYearIdsAction,
  SET_FISCAL_YEAR_IDS,
  SetGroupSortOrderAction,
  SET_GROUP_SORT_ORDER,
  SetMonthSortOrderAction,
  SET_MONTH_SORT_ORDER,
  SortOrder
} from './types'
import { Dimension } from '../../../features/dimension/types/Dimension'
import { BudgetingScenario } from '../../../features/scenario/types/BudgetingScenario'
import { PeriodGroup } from '../../../types/periodGroup/PeriodGroup'
import { Column } from '../periodGroups/hooks'
import { Store } from '../../types'
import { DimensionTree } from '../../../features/dimension/components/DimensionTree'
import { contextCompanyIdSelector } from '../company/selectors'
import dimensionQueryKeys from '../../../features/dimension/queries/dimensionQueryKeys'
import { queryClient } from '../../../services/api/queryClient'
import { currentUserIdSelector } from '../../session/currentUser/selectors'
import {
  loadFiscalYearColumnPreferences,
  saveFiscalYearColumnPreferences
} from '../../../features/contextFilters/utils/fiscalYearColumnPreferences'

export const setFiscalYearFilter = (payload: FiscalYear | null): SetFiscalYearFilterAction => ({
  type: SET_FISCAL_YEAR_FILTER,
  payload
})

export const setCumulativeFilter = (payload: any): SetCumulativeFilterAction => ({
  type: SET_CUMULATIVE_FILTER,
  payload
})

export const toggleDisplayEmptyRows = (): ToggleDisplayEmptyRowsAction => ({
  type: TOGGLE_DISPLAY_EMPTY_ROWS
})

export const setDataTypeFilter = (payload: ReportDataType[]): SetDataTypeFilterAction => ({
  type: SET_DATATYPE_FILTER,
  payload
})

export const toggleDataTypeFilter = (payload: ReportDataType): ToggleDataTypeFilterAction => ({
  type: TOGGLE_DATATYPE_FILTER,
  payload
})

export const setContextFilter = (payload: {
  dimension?: Dimension
  dimensions?: string[]
  budgetingScenario?: BudgetingScenario
  periodGroups?: PeriodGroup[]
}): SetContextFilterAction => ({
  type: SET_CONTEXT_FILTER,
  payload
})

export const setDimensionsFilter = (payload?: string[]): SetDimensionsFilterAction => ({
  type: SET_DIMENSIONS_FILTER,
  payload
})

export const setDimensionsQuery = (payload?: string): SetDimensionsQueryAction => ({
  type: SET_DIMENSIONS_QUERY,
  payload
})

export const setBudgetingScenarioFilter = (payload?: BudgetingScenario): SetBudgetingScenarioFilterAction => ({
  type: SET_BUDGETING_SCENARIO_FILTER,
  payload
})

export const setPeriodGroups = (payload?: PeriodGroup[]): SetPeriodGroupsAction => ({
  type: SET_PERIOD_GROUPS,
  payload
})

export const addPeriodGroup = (payload: PeriodGroup): AddPeriodGroupAction => ({
  type: ADD_PERIOD_GROUP,
  payload
})

export const removePeriodGroup = (payload: PeriodGroup): RemovePeriodGroupAction => ({
  type: REMOVE_PERIOD_GROUP,
  payload
})

export const setPeriodGroupSelection = (payload?: PeriodGroupSelection): SetPeriodGroupSelectionAction => ({
  type: SET_PERIOD_GROUP_SELECTION,
  payload
})

export const setFiscalYearIds = (companyId: string, fiscalYearIds?: number[]): SetFiscalYearIdsAction => ({
  type: SET_FISCAL_YEAR_IDS,
  payload: { companyId, fiscalYearIds }
})

export const setGroupSortOrder = (payload: SortOrder): SetGroupSortOrderAction => ({
  type: SET_GROUP_SORT_ORDER,
  payload
})

export const setMonthSortOrder = (payload: SortOrder): SetMonthSortOrderAction => ({
  type: SET_MONTH_SORT_ORDER,
  payload
})

export const setPeriodGroupFilter = (payload?: FiscalYear | PeriodGroup): SetPeriodGroupFilterAction => ({
  type: SET_PERIOD_GROUP_FILTER,
  payload
})

export const toggleCumulativeFilter = (payload: Column): ToggleCumulativeFilterAction => ({
  type: TOGGLE_CUMULATIVE_FILTER,
  payload
})

export const resetContextFilter = (): ResetContextFilterAction => ({
  type: RESET_CONTEXT_FILTER
})

export const updateDimensionFilterAction = (
  dimensionsFilters: string[]
): ThunkAction<void, Store, null, Action<string>> => {
  return (dispatch, getState) => {
    const store: Store = getState()
    const companyId = contextCompanyIdSelector(store)
    const queryKey = dimensionQueryKeys.list({ companyId })
    const dimensionList = queryClient.getQueryData<Dimension[]>(queryKey)

    let query: string | undefined
    if (dimensionList?.length) {
      const tree = new DimensionTree()
      tree.build(dimensionList)
      query = tree.getQuery(dimensionsFilters)
    }

    dispatch(setDimensionsQuery(query))
    dispatch(setDimensionsFilter(dimensionsFilters))
  }
}

export const updateContextFilterAction = (payload: {
  dimension?: Dimension
  dimensions?: string[]
  budgetingScenario?: BudgetingScenario
  periodGroups?: PeriodGroup[]
}): ThunkAction<void, Store, null, Action<string>> => {
  return (dispatch, getState) => {
    const store: Store = getState()
    const companyId = contextCompanyIdSelector(store)
    const queryKey = dimensionQueryKeys.list({ companyId })

    // get dimensionList data from queryCache
    const dimensionList = queryClient.getQueryData<Dimension[]>(queryKey)

    const tree = new DimensionTree()

    const dims = payload.dimensions
    if (dims !== undefined) {
      if (dims.length === 0) {
        // Clear stale query when dimensions are cleared explicitly.
        dispatch(setDimensionsQuery(undefined))
      } else if (dimensionList) {
        tree.build(dimensionList)
        const query = tree.getQuery(dims)
        dispatch(setDimensionsQuery(query))
      } else {
        // No dimension data in cache → cannot construct a valid query; clear it.
        dispatch(setDimensionsQuery(undefined))
      }
    }

    dispatch(setContextFilter(payload))
  }
}

/**
 * Applies fiscal year column choices (shown fiscal years + sort orders) and remembers them
 * for the current user in this browser.
 */
export const applyFiscalYearColumnPreferences = (
  companyId: string,
  fiscalYearIds: number[] | undefined,
  groupSortOrder: SortOrder,
  monthSortOrder: SortOrder
): ThunkAction<void, Store, null, Action<string>> => {
  return (dispatch, getState) => {
    dispatch(setFiscalYearIds(companyId, fiscalYearIds))
    dispatch(setGroupSortOrder(groupSortOrder))
    dispatch(setMonthSortOrder(monthSortOrder))

    const store: Store = getState()
    const userId = currentUserIdSelector(store)
    if (!userId) return
    const { filters } = store.context
    saveFiscalYearColumnPreferences(userId, {
      fiscalYearIdsByCompany: filters.fiscalYearIdsByCompany ?? {},
      groupSortOrder: filters.groupSortOrder ?? 'asc',
      monthSortOrder: filters.monthSortOrder ?? 'asc'
    })
  }
}

/** Restores the user's remembered fiscal year column choices, e.g. after login. */
export const restoreFiscalYearColumnPreferences = (userId: number): ThunkAction<void, Store, null, Action<string>> => {
  return dispatch => {
    const preferences = loadFiscalYearColumnPreferences(userId)
    if (!preferences) return
    Object.entries(preferences.fiscalYearIdsByCompany).forEach(([companyId, fiscalYearIds]) => {
      dispatch(setFiscalYearIds(companyId, fiscalYearIds))
    })
    dispatch(setGroupSortOrder(preferences.groupSortOrder))
    dispatch(setMonthSortOrder(preferences.monthSortOrder))
  }
}
